import { css } from '@emotion/css';
import { Alert, Badge, Button, Flex, Layout, theme } from 'antd';
import type React from 'react';
import { type ReactNode, useMemo } from 'react';
import logo from '../assets/logo.svg';
import { useAppVersionStore } from '../hooks/store/useAppVersionStore.ts';
import type { Theme } from '../hooks/store/useThemeStore.ts';
import { AppSwitcher } from './AppSwitcher.tsx';
import { HelpMenu } from './HelpMenu';

const { useToken } = theme;
const { Header } = Layout;

const envTag: string = window._env_.ENV_TAG;

interface AppHeaderProps {
  actions: ReactNode;
  userActions: ReactNode;
  theme: Theme;
}

const calcHeaderTextColor = (hex?: string) => {
  if (hex?.length !== 7) {
    // default for transparent color and no hex string beginning with #
    return 'inherit';
  }
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? 'inherit' : '#f6f6f6';
};

export const AppHeader: React.FC<AppHeaderProps> = ({ actions, userActions, theme }) => {
  const { token } = useToken();
  const { bannerVisible } = useAppVersionStore();
  const headerTextColor = useMemo(() => calcHeaderTextColor(token.Layout?.headerBg), [token]);

  const styles = {
    header: css`
      display: flex;
      position: relative;
      box-sizing: border-box;
      padding-inline: ${token.paddingLG}px !important;
      color: ${headerTextColor};

      .ant-btn {
        color: ${headerTextColor};

        :hover,
        :active {
          color: ${headerTextColor} !important;
        }
      }
    `,
    logo: css`
      img {
        width: 200px;
      }
    `,
    content: css`
      flex-grow: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    `,
    versionAlert: css`
      background-color: ${token.colorError};
      border: none;
      color: white;

      .ant-btn,
      .ant-btn:hover,
      .ant-btn:active {
        color: ${token.colorError} !important;
      }

      .ant-btn {
        margin-left: ${token.marginSM}px;
        animation: pulsate 3s ease-out;
        animation-iteration-count: infinite;
        opacity: 1;

        @keyframes pulsate {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            opacity: 1;
          }
        }
      }
    `,
    actions: css`
      line-height: initial;
      display: flex;
      align-items: center;
    `,
    help: css`
      margin-left: ${token.paddingLG}px;
    `,
  };

  return (
    <Header className={styles.header}>
      <div className={styles.logo}>
        {envTag && envTag !== 'prod' ? (
          <Badge.Ribbon text={envTag} color="red">
            <img src={theme.logoData ? theme.logoData : logo} alt="labcomplete® Anforderungen" />
          </Badge.Ribbon>
        ) : (
          <img src={theme.logoData ? theme.logoData : logo} alt="labcomplete® Anforderungen" />
        )}
      </div>
      <div className={styles.content}>
        {bannerVisible() ? (
          <Alert
            title={<strong>Neue Version von labcomplete® verfügbar!</strong>}
            type="error"
            action={<Button onClick={() => window.location.reload()}>Jetzt aktualisieren</Button>}
            className={styles.versionAlert}
          />
        ) : (
          <AppSwitcher />
        )}
      </div>
      <div className={styles.actions}>
        <Flex gap="small">
          <Flex>{actions}</Flex>
          {userActions}
        </Flex>
        <HelpMenu className={styles.help} />
      </div>
    </Header>
  );
};
